/**
 * IntroductionBanner — bandeau global affiché quand l'org est sur le plan
 * "introduction" (mode découverte volontairement réduit).
 *
 * V23.15.70 — Discret mais permanent. Deux CTA :
 *   - lien mailto vers contact@noamworks.ch pour demander l'accès complet
 *   - bouton "Fermer temporairement" (masque le bandeau jusqu'à reload)
 */
import { useEffect, useState } from "react";
import { Sparkles, X, Mail } from "lucide-react";
import { api } from "@/lib/api";

const SUPPORT_EMAIL = "contact@noamworks.ch";
const DISMISS_KEY = "noam_intro_banner_dismissed";

export default function IntroductionBanner() {
  const [plan, setPlan] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    api.get("/workspace/me")
      .then((r) => setPlan(r.data?.plan || r.data?.organisation?.plan || null))
      .catch(() => setPlan(null));
  }, []);

  if (plan !== "introduction" || dismissed) return null;

  const mailBody = encodeURIComponent(
    `Bonjour,\n\nJ'utilise actuellement Noam en mode découverte et je souhaiterais accéder à l'ensemble des modules.\n\nMerci d'avance,\n`
  );
  const mailSubject = encodeURIComponent("Débloquer l'accès complet à Noam");
  const mailHref = `mailto:${SUPPORT_EMAIL}?subject=${mailSubject}&body=${mailBody}`;

  return (
    <div
      className="w-full bg-gradient-to-r from-[#EF9F27]/15 via-[#EF9F27]/10 to-[#7F77DD]/15 border-b border-[#EF9F27]/30 text-white"
      data-testid="introduction-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 py-2 text-xs sm:text-[13px]">
        <Sparkles className="w-4 h-4 text-[#EF9F27] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-[#EF9F27]">Mode découverte.</span>{" "}
          <span className="text-zinc-200">Certaines fonctionnalités sont volontairement réduites pour vous permettre de prendre en main Noam à votre rythme. Pour débloquer l'accès complet, écrivez-nous à </span>
          <a
            href={mailHref}
            className="inline-flex items-center gap-1 text-[#EF9F27] hover:text-white underline underline-offset-2 font-medium"
            data-testid="introduction-banner-mail-link"
          >
            <Mail className="w-3 h-3" />
            {SUPPORT_EMAIL}
          </a>
          <span className="text-zinc-200">.</span>
        </div>
        <button
          onClick={() => {
            try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
            setDismissed(true);
          }}
          className="text-zinc-400 hover:text-white flex-shrink-0"
          title="Masquer jusqu'au prochain rechargement"
          data-testid="introduction-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
